// /feedback/<token> — public stay/exit interview chat surface.
//
// Token IS the auth — no login required. Rendered for the URL the
// coordinator emails/SMS-es to a member. Sonnet drives a structured
// conversation (system prompt lives in the worker handler) and the
// transcript is encrypted at rest with role-gated read access.
//
// Plan:    docs/plans/2026-04-19-ai-ml-execution-plan.md Unit 12
// Research: docs/research/2026-04-19-dcvfd-ai-ml-capability-research-q2-q4-2026.md §5.2

import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";

interface Message {
  role: "assistant" | "user";
  text: string;
  ts: string;
}

interface InterviewState {
  interview_id: number;
  trigger_type: string;
  started: boolean;
  completed: boolean;
  message_count: number;
  transcript: Message[];
}

export default function Interview() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<InterviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [draft, setDraft] = useState("");
  const transcriptRef = useRef<HTMLDivElement>(null);

  // Initial load — fetch state, kick off opening message if not started
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/feedback/${token}`);
        if (!res.ok) {
          throw new Error(
            res.status === 403
              ? "This interview link has expired."
              : `Couldn't load interview (${res.status}).`,
          );
        }
        const data = (await res.json()) as InterviewState;
        if (cancelled) return;
        setState(data);

        // If no messages yet, request the assistant's opening turn (empty user text).
        if (!cancelled && (data.transcript?.length ?? 0) === 0) {
          await sendInternal(token, "", data, setState);
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Auto-scroll to latest turn
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [state?.transcript.length]);

  const send = useCallback(async () => {
    if (!token || pending || !state || state.completed) return;
    const text = draft.trim();
    if (!text) return;
    setPending(true);
    setError(null);
    try {
      await sendInternal(token, text, state, setState);
      setDraft("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Send failed");
    } finally {
      setPending(false);
    }
  }, [token, pending, state, draft]);

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-lg font-bold text-red-700">
            Can't load this interview
          </h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <p className="text-sm text-zinc-500">Loading…</p>
      </div>
    );
  }

  const visibleTranscript = state.transcript.map((m) => ({
    ...m,
    text: m.text.replace("[INTERVIEW_COMPLETE]", "").trim(),
  }));

  return (
    <div className="min-h-dvh bg-zinc-50 dark:bg-zinc-950 flex flex-col">
      <header className="px-4 md:px-6 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-base font-bold text-zinc-900 dark:text-zinc-50">
            DCVFD member check-in
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            A short conversation — your responses go to the R&amp;R co-chairs
            and aren't shared more broadly. Take as much time as you need; the
            link stays open for 7 days.
          </p>
        </div>
      </header>

      <div
        ref={transcriptRef}
        className="flex-1 overflow-y-auto px-4 md:px-6 py-4"
      >
        <div className="max-w-2xl mx-auto space-y-4">
          {visibleTranscript.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={[
                  "max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap",
                  m.role === "user"
                    ? "bg-emerald-600 text-white"
                    : "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 border border-zinc-200 dark:border-zinc-700",
                ].join(" ")}
              >
                {m.text}
              </div>
            </div>
          ))}
          {pending && (
            <div className="flex justify-start">
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-2xl px-4 py-3 text-sm text-zinc-400 animate-pulse">
                …
              </div>
            </div>
          )}
        </div>
      </div>

      <footer className="px-4 md:px-6 py-3 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="max-w-2xl mx-auto">
          {state.completed ? (
            <div className="text-center text-sm text-zinc-500 py-2">
              Thanks — this conversation is complete. The team will review what
              you shared.
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={pending}
                placeholder="Type your reply…"
                className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                autoFocus
              />
              <button
                type="submit"
                disabled={pending || draft.trim().length === 0}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send
              </button>
            </form>
          )}
        </div>
      </footer>
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────

async function sendInternal(
  token: string,
  text: string,
  state: InterviewState,
  setState: (s: InterviewState) => void,
): Promise<void> {
  // Optimistically append the user message
  if (text.length > 0) {
    setState({
      ...state,
      transcript: [
        ...state.transcript,
        { role: "user", text, ts: new Date().toISOString() },
      ],
      message_count: state.transcript.length + 1,
    });
  }

  const res = await fetch(`/api/feedback/${token}/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    throw new Error(`Send failed (${res.status})`);
  }
  const _reply = (await res.json()) as {
    assistant_text: string;
    is_complete: boolean;
    message_count: number;
  };

  // Refetch to pull authoritative transcript including the newly-appended turn
  const stateRes = await fetch(`/api/feedback/${token}`);
  if (stateRes.ok) {
    setState((await stateRes.json()) as InterviewState);
  }
}
